import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// mui
import { IconButton, CircularProgress, useTheme } from '@material-ui/core';
import { CheckCircle, Cancel } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
// components
import MentionsTextField from 'components/MentionsTextField';
// actions
import { updateCommentAction } from 'store/actions/commentActions';
// selectors
import getLoadingFromState from 'store/selectors/getLoadingFromState';
import { usePrevious } from 'helpers/hooks/usePrevious';
import getStatusFromState from 'store/selectors/getStatusFromState';
type Props = {
  id: number;
  body: string;
  onExit: (...args: any[]) => any;
};
export default function UpdateCommentInput({ id, body, onExit }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const loading = useSelector(getLoadingFromState('comment', id, false));
  const status = useSelector(getStatusFromState('comment', id, 'success'));
  const previousLoading = usePrevious(loading);
  const [updatedBody, setUpdatedBody] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const theme = useTheme();
  const updateComment = () =>
    dispatch(updateCommentAction({ id, fields: { body: updatedBody } }));
  const handleChange = (_: unknown, newValue: string) =>
    setUpdatedBody(newValue);
  const handleKeyDown = (event: any) => {
    if (event.key === 'Escape') onExit();
    else if ((event.ctrlKey || event.metaKey) && event.key === 'Enter')
      updateComment();
  };
  useEffect(() => {
    setUpdatedBody(body);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, theme.transitions.duration.leavingScreen);
  }, [body, theme]);
  useEffect(() => {
    if (previousLoading && !loading && status === 'success') onExit();
  }, [previousLoading, loading, status, onExit]);
  return (
    <MentionsTextField
      label='Edit comment'
      inputRef={inputRef}
      value={updatedBody}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      containerClassName={classes.updateInput}
      endAdornment={
        loading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <IconButton size='small' onClick={onExit} aria-label='Cancel edit'>
              <Cancel fontSize='small' />
            </IconButton>
            {body !== updatedBody?.trim() && (
              <IconButton
                size='small'
                onClick={updateComment}
                aria-label='Save comment'
              >
                <CheckCircle fontSize='small' />
              </IconButton>
            )}
          </>
        )
      }
    />
  );
}
const useStyles = makeStyles(() => ({
  updateInput: {
    paddingTop: 8,
    paddingBottom: 8,
  },
}));
